/**
 * Step 3 — Items editor.
 *
 * Auto-fills item[0].description with "Miete <Monat> <Jahr>" if type=rent
 * and description is blank, on entry to Step 3. Subsequent edits to type
 * or period do NOT overwrite — admin discretion.
 */
import React, { useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { MONTH_NAMES_DE } from "./MONTH_NAMES";
import { getItemFieldErrors } from "./validation";

const ITEM_TYPES = [
  { value: "rent", label: "Miete" },
  { value: "utilities", label: "Nebenkosten" },
  { value: "deposit", label: "Kaution" },
  { value: "cleaning", label: "Reinigung" },
  { value: "custom", label: "Sonstiges" },
];

const labelCls =
  "mb-1 block text-[10px] font-semibold uppercase tracking-[1px] text-[#64748b] dark:text-[#6b7a9a]";
const baseInputCls =
  "h-[38px] w-full rounded-[8px] border bg-slate-100 px-2 text-sm text-[#0f172a] outline-none dark:bg-[#111520] dark:text-[#eef2ff]";
const okBorder = "border-black/10 dark:border-white/[0.08]";
const errBorder = "border-red-400 dark:border-red-500/60";

function fmtChf(n) {
  const v = Number.isFinite(n) ? n : 0;
  return `CHF ${v.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function Step3Items({ state, updateState }) {
  // One-time auto-fill on Step 3 entry.
  useEffect(() => {
    if (state.step !== 3) return;
    const first = state.items?.[0];
    if (!first) return;
    if (first.type !== "rent") return;
    if (first.description && first.description.trim()) return;
    const desc = `Miete ${MONTH_NAMES_DE[state.billingMonth - 1]} ${
      state.billingYear
    }`;
    const next = [...state.items];
    next[0] = { ...next[0], description: desc };
    updateState({ items: next });
    // eslint-disable-next-line
  }, [state.step]);

  function patchItem(index, patch) {
    const next = state.items.map((it, i) =>
      i === index ? { ...it, ...patch } : it
    );
    updateState({ items: next });
  }

  function addItem() {
    updateState({
      items: [
        ...state.items,
        {
          type: "custom",
          description: "",
          quantity: 1,
          unit_price_chf: 0,
        },
      ],
    });
  }

  function removeItem(index) {
    if (state.items.length <= 1) return;
    updateState({
      items: state.items.filter((_, i) => i !== index),
    });
  }

  const total = state.items.reduce((sum, it) => {
    const q = parseFloat(it.quantity);
    const p = parseFloat(it.unit_price_chf);
    if (!Number.isFinite(q) || !Number.isFinite(p)) return sum;
    return sum + q * p;
  }, 0);

  return (
    <div className="space-y-4">
      <ul className="space-y-3">
        {state.items.map((item, index) => {
          const errors = getItemFieldErrors(item);
          const qty = parseFloat(item.quantity);
          const price = parseFloat(item.unit_price_chf);
          const rowAmount =
            Number.isFinite(qty) && Number.isFinite(price) ? qty * price : 0;
          return (
            <li
              key={index}
              className="rounded-[10px] border border-black/10 bg-white p-3 dark:border-white/[0.07] dark:bg-[#0f1320]"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Typ</label>
                  <select
                    value={item.type}
                    onChange={(e) =>
                      patchItem(index, { type: e.target.value })
                    }
                    className={`${baseInputCls} ${
                      errors.type ? errBorder : okBorder
                    }`}
                  >
                    {ITEM_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-4">
                  <label className={labelCls}>Beschreibung</label>
                  <input
                    type="text"
                    value={item.description ?? ""}
                    onChange={(e) =>
                      patchItem(index, { description: e.target.value })
                    }
                    className={`${baseInputCls} ${okBorder}`}
                    placeholder="z.B. Miete April 2026"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Anzahl</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={item.quantity}
                    onChange={(e) =>
                      patchItem(index, { quantity: e.target.value })
                    }
                    className={`${baseInputCls} ${
                      errors.quantity ? errBorder : okBorder
                    }`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Einzelpreis (CHF)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.unit_price_chf}
                    onChange={(e) =>
                      patchItem(index, { unit_price_chf: e.target.value })
                    }
                    className={`${baseInputCls} ${
                      errors.unit_price_chf ? errBorder : okBorder
                    }`}
                  />
                </div>
                <div className="flex items-end justify-between gap-2 sm:col-span-2">
                  <div>
                    <div className={labelCls}>Betrag</div>
                    <div className="text-sm font-semibold text-[#0f172a] dark:text-[#eef2ff]">
                      {fmtChf(rowAmount)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    disabled={state.items.length <= 1}
                    className="rounded-[8px] p-2 text-[#94a3b8] hover:bg-red-500/10 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-40"
                    title="Position entfernen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {(errors.type || errors.quantity || errors.unit_price_chf) ? (
                <div className="mt-2 text-[12px] text-red-600 dark:text-red-400">
                  {errors.type ? "Typ erforderlich. " : null}
                  {errors.quantity ? "Anzahl muss > 0 sein. " : null}
                  {errors.unit_price_chf
                    ? "Einzelpreis muss eine gültige Zahl ≥ 0 sein."
                    : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        onClick={addItem}
        className="inline-flex items-center gap-2 rounded-[8px] border border-dashed border-black/15 px-3 py-2 text-sm text-[#64748b] hover:border-black/30 hover:text-[#0f172a] dark:border-white/[0.12] dark:text-[#6b7a9a] dark:hover:border-white/30 dark:hover:text-[#eef2ff]"
      >
        <Plus className="h-4 w-4" />
        Position hinzufügen
      </button>

      <div className="flex items-center justify-between border-t border-black/10 pt-3 dark:border-white/[0.07]">
        <span className="text-[11px] font-semibold uppercase tracking-[1px] text-[#64748b] dark:text-[#6b7a9a]">
          Total
        </span>
        <span className="text-[18px] font-bold text-[#0f172a] dark:text-[#eef2ff]">
          {fmtChf(total)}
        </span>
      </div>
    </div>
  );
}
